import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PassageAnswer } from '@examing/types';
import { RenderedHtml } from 'modules/editor';
import { PassagePlayer } from 'components/questions/PassagePlayer';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from 'lib/utils';
import { ExamAnswerArea, PROMPT_OWNED_TYPES } from './ExamAnswerArea';
import { ListeningSectionView } from './ListeningSectionView';
import { SpeakingSectionView } from './SpeakingSectionView';
import { WritingTask } from './WritingTask';
import { PassageHighlighter } from './PassageHighlighter';
import { useSaveWord } from 'modules/vocabulary';
import { loadHighlights, saveHighlights, type HighlightsByPassage } from './highlightStore';
import { loadSectionPart, saveSectionPart } from './sectionPart';
import type { CandidateQuestion, CandidateSection, SpeakingAnswer } from '../../types';
import { buildSectionReview, subQuestionIds } from './sectionReview';
import { useExamFlags } from './ExamFlags';
import { useQuestionTime } from './useQuestionTime';
import { countWords } from '../../constants';

interface Props {
  attemptId: string;
  section: CandidateSection;
  answers: Map<string, unknown>;
  onAnswer: (questionId: string, value: unknown) => void;
  /** Speaking recordings are stored by their upload; this only mirrors them locally. */
  onRecorded: (questionId: string, value: SpeakingAnswer) => void;
  /** Time spent on the open Reading passage or Writing task, saved with the answers. */
  onMeta?: (questionId: string, meta: { timeSpentSec?: number }) => void;
  /** Lets the session ask for the running time just before it sends. */
  registerSettle?: (settle: () => void) => () => void;
  /** Open this question (from the review grid); bump `seq` to repeat a jump to the same one. */
  jump?: { questionId: string; seq: number } | null;
}

/** Stacked questions with IELTS-style numbering (a passage counts each sub-question). */
function StackedQuestions({
  questions,
  answers,
  onAnswer,
}: {
  questions: CandidateQuestion[];
  answers: Map<string, unknown>;
  onAnswer: Props['onAnswer'];
}) {
  const { t } = useTranslation();
  let number = 0;
  return (
    <ol className="mx-auto max-w-3xl space-y-6 p-5 sm:p-8">
      {questions.map((q) => {
        number += 1;
        const label = number;
        return (
          <li key={q.id} id={`exam-q-${q.id}`} className="scroll-mt-24 rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
            <div className="flex gap-3">
              <span className="mt-0.5 flex h-7 min-w-[1.75rem] shrink-0 items-center justify-center rounded-md border border-gray-900 px-1.5 text-sm font-bold text-gray-900">
                {label}
              </span>
              <div className="min-w-0 flex-1 space-y-3">
                {!PROMPT_OWNED_TYPES.has(q.type) && (
                  <div className="text-[15px] leading-relaxed text-gray-900">
                    <RenderedHtml html={q.prompt || ''} />
                  </div>
                )}
                <ExamAnswerArea question={q} value={answers.get(q.id)} onChange={(v) => onAnswer(q.id, v)} />
              </div>
            </div>
          </li>
        );
      })}
      {!questions.length && <p className="text-center text-gray-600">{t('exam.empty_section')}</p>}
    </ol>
  );
}

export function IeltsSectionView({ attemptId, section, answers, onAnswer, onRecorded, onMeta, registerSettle, jump }: Props) {
  const { t } = useTranslation();
  const { flagged, toggle: toggleFlag } = useExamFlags();
  const saveWord = useSaveWord();
  const passages = useMemo(() => section.questions.filter((q) => q.type === 'PASSAGE'), [section.questions]);
  const others = useMemo(() => section.questions.filter((q) => q.type !== 'PASSAGE'), [section.questions]);
  // The open Reading passage or Writing task; a reload opens the same one.
  const ownsPart = section.kind === 'IELTS_READING' || section.kind === 'IELTS_WRITING';
  const partCount = section.kind === 'IELTS_WRITING' ? section.questions.length : passages.length;
  const [part, setPart] = useState(() => (ownsPart ? loadSectionPart(attemptId, section.id, partCount) : 0));
  useEffect(() => {
    if (ownsPart) saveSectionPart(attemptId, section.id, part);
  }, [ownsPart, attemptId, section.id, part]);
  // Time is kept per passage and per task: the answer of a passage is stored on the passage, not on its questions.
  const openPart = section.kind === 'IELTS_READING' ? passages[Math.min(part, passages.length - 1)] : section.kind === 'IELTS_WRITING' ? section.questions[Math.min(part, section.questions.length - 1)] : undefined;
  useQuestionTime(openPart?.id, onMeta, registerSettle);
  // Keyed by passage and page. Kept here so highlights survive part switches,
  // and dropped with the section, as in the computer-delivered test. Saved in
  // this browser so a reload keeps them.
  const [highlights, setHighlights] = useState<HighlightsByPassage>(() => loadHighlights(attemptId, section.id));
  useEffect(() => {
    saveHighlights(attemptId, section.id, highlights);
  }, [attemptId, section.id, highlights]);

  // Opening a question from the review grid: the passage or task that holds it, then the question itself.
  const [focus, setFocus] = useState<{ questionId: string; seq: number } | null>(null);
  useEffect(() => {
    if (!jump) return;
    if (section.kind === 'IELTS_READING') {
      const inPassage = passages.findIndex((p) => subQuestionIds(p).includes(jump.questionId));
      if (inPassage >= 0) {
        setPart(inPassage);
        setFocus(jump);
      } else if (others.some((q) => q.id === jump.questionId)) {
        setPart(Math.max(0, passages.length - 1));
        setTimeout(() => document.getElementById(`exam-q-${jump.questionId}`)?.scrollIntoView({ block: 'nearest' }), 0);
      }
    } else if (section.kind === 'IELTS_WRITING') {
      const task = section.questions.findIndex((q) => q.id === jump.questionId);
      if (task >= 0) setPart(task);
    } else {
      document.getElementById(`exam-q-${jump.questionId}`)?.scrollIntoView({ block: 'nearest' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jump?.seq]);
  // The player takes the request on mount or change; clearing it keeps a later return to this passage from replaying it.
  useEffect(() => {
    if (!focus) return undefined;
    const id = setTimeout(() => setFocus(null), 0);
    return () => clearTimeout(id);
  }, [focus]);

  if (section.kind === 'IELTS_WRITING') {
    const tasks = section.questions;
    const current = tasks[Math.min(part, tasks.length - 1)];
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {current ? (
          <WritingTask
            key={current.id}
            question={current}
            value={typeof answers.get(current.id) === 'string' ? (answers.get(current.id) as string) : ''}
            onChange={(v) => onAnswer(current.id, v)}
          />
        ) : (
          <p className="p-8 text-center text-gray-600">{t('exam.empty_section')}</p>
        )}
        {tasks.length > 1 && (
          <PartTabs
            labels={tasks.map((q, i) =>
              q.writingTask === 'TASK_1' ? t('exam.writing.task1') : q.writingTask === 'TASK_2' ? t('exam.writing.task2') : t('exam.part', { n: i + 1 }),
            )}
            active={part}
            onSelect={setPart}
            done={tasks.map((q) => typeof answers.get(q.id) === 'string' && (answers.get(q.id) as string).trim() !== '')}
            meta={tasks.map((q) => t('exam.writing.word_count', { count: countWords(typeof answers.get(q.id) === 'string' ? (answers.get(q.id) as string) : '') }))}
          />
        )}
      </div>
    );
  }

  if (section.kind === 'IELTS_READING' && passages.length) {
    const passageCounts = buildSectionReview(section, answers).groups.map((g) => `${g.items.filter((i) => i.answered).length}/${g.items.length}`);
    const current = passages[Math.min(part, passages.length - 1)];
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {/* Keyed by passage so switching parts opens the new one at its top. */}
        <div key={current.id} className="min-h-0 flex-1 overflow-y-auto px-4 py-3 sm:px-7 sm:py-4">
          <PassagePlayer
            passageText={current.passageText || ''}
            passageTitle={current.passageTitle}
            subQuestions={current.subQuestions ?? []}
            pages={current.pages}
            value={(answers.get(current.id) as PassageAnswer) || {}}
            onChange={(v) => onAnswer(current.id, v)}
            numberOffset={passages.slice(0, Math.min(part, passages.length - 1)).reduce((sum, p) => sum + subQuestionIds(p).length, 0)}
            focusRequest={focus ?? undefined}
            flaggedIds={flagged}
            onToggleFlag={toggleFlag}
            fillHeight
            renderPassage={(passage, pageIndex) => {
              const key = `${current.id}:${pageIndex}`;
              return (
                <PassageHighlighter
                  contentKey={key}
                  highlights={highlights[key] ?? []}
                  onChange={(update) => setHighlights((prev) => ({ ...prev, [key]: update(prev[key] ?? []) }))}
                  onSaveWord={saveWord}
                >
                  {passage}
                </PassageHighlighter>
              );
            }}
          />
          {others.length > 0 && part === passages.length - 1 && (
            <StackedQuestions questions={others} answers={answers} onAnswer={onAnswer} />
          )}
        </div>
        {passages.length > 1 && (
          <PartTabs
            labels={passages.map((p, i) => p.passageTitle || t('exam.part', { n: i + 1 }))}
            active={part}
            onSelect={setPart}
            done={passages.map((p) => {
              const v = answers.get(p.id);
              return Boolean(v && typeof v === 'object' && Object.keys(v as object).length >= (p.subQuestions?.length ?? 1));
            })}
            meta={passageCounts}
          />
        )}
      </div>
    );
  }

  if (section.kind === 'IELTS_SPEAKING') {
    return <SpeakingSectionView attemptId={attemptId} section={section} answers={answers} onRecorded={onRecorded} />;
  }

  if (section.kind === 'IELTS_LISTENING') {
    return <ListeningSectionView attemptId={attemptId} section={section} answers={answers} onAnswer={onAnswer} jump={jump} />;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto bg-[#F6F5F1]">
        <StackedQuestions questions={section.questions} answers={answers} onAnswer={onAnswer} />
      </div>
    </div>
  );
}

function PartTabs({
  labels,
  active,
  onSelect,
  done,
  meta,
}: {
  labels: string[];
  active: number;
  onSelect: (i: number) => void;
  done: boolean[];
  /** A short line under each label: answers given, or words written. */
  meta: string[];
}) {
  const { t } = useTranslation();
  return (
    <div className="flex shrink-0 items-center gap-3.5 border-t border-[#E6E9F2] bg-white px-4 pb-[max(0.625rem,env(safe-area-inset-bottom))] pt-2.5 shadow-[0_-8px_24px_-18px_rgba(16,24,43,0.4)] sm:px-6">
      <nav className="flex min-w-0 flex-1 gap-2.5 overflow-x-auto py-1" aria-label={t('exam.parts')}>
        {labels.map((label, i) => (
          <button
            key={label + i}
            type="button"
            onClick={() => onSelect(i)}
            aria-current={i === active ? 'page' : undefined}
            className={cn(
              'flex min-w-[128px] shrink-0 flex-col gap-1 rounded-xl border px-4 py-2 text-left transition-colors focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#10182B] sm:min-w-[176px]',
              i === active ? 'border-[#10182B] bg-white shadow-[0_0_0_2px_#EEF1F8]' : 'border-[#E6E9F2] bg-[#FBFCFE] hover:bg-white',
            )}
          >
            <b className="max-w-[16rem] truncate text-sm text-[#10182B]">
              {label}
              {done[i] && <span className="ml-1.5 text-xs text-green-700">✓</span>}
            </b>
            <span className="text-xs text-[#646D84]">{meta[i]}</span>
          </button>
        ))}
      </nav>
      <div className="flex shrink-0 items-center gap-2">
        <StepButton direction="prev" disabled={active <= 0} onClick={() => onSelect(active - 1)} />
        <StepButton direction="next" disabled={active >= labels.length - 1} onClick={() => onSelect(active + 1)} />
      </div>
    </div>
  );
}

function StepButton({ direction, disabled, onClick }: { direction: 'prev' | 'next'; disabled: boolean; onClick: () => void }) {
  const { t } = useTranslation();
  const next = direction === 'next';
  const label = next ? t('exam.nav.next') : t('exam.nav.previous');
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        'flex h-11 w-11 items-center justify-center rounded-xl border-[1.5px] border-[#10182B] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#10182B] disabled:opacity-40',
        next ? 'bg-[#10182B] text-white' : 'bg-white text-[#10182B]',
      )}
    >
      {next ? <ChevronRight className="h-5 w-5" aria-hidden /> : <ChevronLeft className="h-5 w-5" aria-hidden />}
    </button>
  );
}

export default IeltsSectionView;
